/**
 * Définition des TRAJETS de transfert (Compact SEO bas-de-funnel).
 *
 * Chaque entrée = une page "transfert A → B" à intention d'achat chaude
 * (ex. recherche "prix transfert aéroport nice cannes"). L'actif propriétaire
 * qui rend ces pages uniques et inattaquables (anti "scaled content abuse") :
 * distance/durée réelles + prix réels (tirés de infos.ts) + contenu spécifique.
 *
 * Dépendances :
 * - `fromId` / `toId` référencent TRANSFER_PLACES (places.ts), eux-mêmes alignés
 *   sur les clés VEHICLES.fixedTransfers (infos.ts) → permet getRoutePrices().
 * - Consommé par `index.ts` (helpers), les pages et les routeurs meta/JSON-LD.
 *
 * ⚠️ Aucun prix ici : les prix vivent dans infos.ts (source unique).
 */

export type TransferFaqItem = { q: string; a: string };

/** Partie localisable d'un trajet (FR dans routes.ts, EN dans routesEn.ts). */
export type TransferRouteContent = {
  lead: string;
  highlights: string[];
  faq: TransferFaqItem[];
  seo: { title: string; description: string; canonical: string };
};

export type TransferRoute = {
  /** Slug d'URL : /chauffeur/transferts/<slug>. Stable, ne pas renommer sans 308. */
  slug: string;
  fromId: string;
  toId: string;
  /** Distance routière réelle approximative (km) — actif propriétaire. */
  distanceKm: number;
  /** Durée de trajet réelle approximative (minutes hors trafic) — actif propriétaire. */
  durationMin: number;
  /** Accroche éditoriale propre au trajet (1-2 phrases). */
  lead: string;
  /** Points spécifiques au trajet (repères, usages, conseils). */
  highlights: string[];
  /**
   * FAQ d'objection d'achat, rendue crawlable (pas d'accordéon). ⚠️ Les
   * questions de prix et de choix du véhicule sont REMPLACÉES au rendu par la
   * version chiffrée de generatedContent.ts : ne pas en réécrire ici.
   */
  faq: TransferFaqItem[];
  /**
   * ⚠️ En FR et EN, `title` et `description` sont RECALCULÉS depuis les prix
   * (generatedContent.ts `buildTransferSeo`). Ceux écrits ici ne servent plus
   * que de repli si un trajet n'a aucun prix. Seul `canonical` fait foi.
   */
  seo: {
    title: string;
    description: string;
    canonical: string;
  };
};

/**
 * Les 7 trajets couverts par la grille tarifaire fixe (infos.ts).
 * Ajouter un trajet ici nécessite d'ajouter la clé correspondante dans
 * VEHICLES.fixedTransfers (les 4 classes) — sinon getRoutePrices() le signale.
 */
export const TRANSFER_ROUTES: TransferRoute[] = [
  {
    slug: "cannes-aeroport-nice",
    fromId: "CANNES",
    toId: "NCE_AIRPORT",
    distanceKm: 27,
    durationMin: 30,
    lead: "Transfert privé entre Cannes et l'aéroport de Nice Côte d'Azur, avec chauffeur, suivi de vol et accueil en bas de l'avion. Tarif forfaitaire fixé à l'avance, sans surprise.",
    highlights: [
      "Suivi de vol en temps réel : le chauffeur ajuste l'heure de prise en charge en cas de retard.",
      "Accueil pancarte au terminal 1 ou 2 selon votre compagnie.",
      "Trajet direct par l'A8, environ 30 minutes hors heures de pointe.",
      "Idéal Festival de Cannes, MIPIM, MIPCOM et arrivées hôtel sur la Croisette.",
    ],
    faq: [
      {
        q: "Combien coûte un transfert Cannes – aéroport de Nice ?",
        a: "Le tarif est forfaitaire et fixé à l'avance selon la catégorie de véhicule. Vous voyez le prix exact par classe dans le tableau ci-dessus, sans frais cachés.",
      },
      {
        q: "Le chauffeur attend-il si mon vol est en retard ?",
        a: "Oui. Nous suivons votre numéro de vol et adaptons l'heure de prise en charge. Un temps de courtoisie de 30 minutes est prévu à l'arrivée aéroport.",
      },
      {
        q: "Combien de temps dure le trajet ?",
        a: "Environ 30 minutes hors heures de pointe, par l'autoroute A8. Prévoyez une marge pendant le Festival de Cannes et les week-ends d'été.",
      },
    ],
    seo: {
      title:
        "Transfert Cannes ⇄ Aéroport de Nice — VTC chauffeur privé | KS Rent Services",
      description:
        "Transfert privé Cannes – aéroport de Nice avec chauffeur, suivi de vol et tarif forfaitaire. Environ 30 min, prix fixe par véhicule. Réservez en ligne.",
      canonical: "/chauffeur/transferts/cannes-aeroport-nice",
    },
  },
  {
    slug: "monaco-aeroport-nice",
    fromId: "MONACO",
    toId: "NCE_AIRPORT",
    distanceKm: 32,
    durationMin: 35,
    lead: "Transfert privé entre Monaco et l'aéroport de Nice Côte d'Azur. Chauffeur en tenue, suivi de vol et tarif forfaitaire, pour vos déplacements business comme vos départs en jet.",
    highlights: [
      "Liaison fréquente pour la clientèle affaires et résidents de la Principauté.",
      "Coordination possible avec l'héliport de Monaco sur demande.",
      "Trajet d'environ 35 minutes par la basse ou moyenne corniche / A8.",
      "Discrétion et ponctualité adaptées aux agendas serrés.",
    ],
    faq: [
      {
        q: "Quel est le tarif d'un transfert Monaco – aéroport de Nice ?",
        a: "Forfait fixe par catégorie de véhicule, affiché ci-dessus. Le prix est garanti à la réservation, péages et accueil inclus.",
      },
      {
        q: "Proposez-vous la liaison vers l'héliport de Monaco ?",
        a: "Oui, nous coordonnons les transferts vers et depuis l'héliport de Monaco. Précisez-le lors de la réservation pour caler les horaires.",
      },
      {
        q: "Le service est-il disponible tôt le matin ou tard le soir ?",
        a: "Oui, selon disponibilité, y compris pour les vols très matinaux ou de nuit. Réservez à l'avance pour garantir le créneau.",
      },
    ],
    seo: {
      title:
        "Transfert Monaco ⇄ Aéroport de Nice — VTC chauffeur privé | KS Rent Services",
      description:
        "Transfert privé Monaco – aéroport de Nice avec chauffeur et tarif forfaitaire. Environ 35 min, prix fixe par véhicule, suivi de vol. Réservez en ligne.",
      canonical: "/chauffeur/transferts/monaco-aeroport-nice",
    },
  },
  {
    slug: "aeroport-nice-saint-tropez",
    fromId: "NCE_AIRPORT",
    toId: "SAINT_TROPEZ",
    distanceKm: 110,
    durationMin: 90,
    lead: "Transfert longue distance entre l'aéroport de Nice et Saint-Tropez, avec chauffeur privé. Confort haut de gamme sur près de deux heures de route, tarif forfaitaire tout compris.",
    highlights: [
      "Trajet d'environ 1h30 à 2h selon le trafic estival sur l'A8 puis la route du golfe.",
      "Véhicules spacieux recommandés (Class V) pour bagages et groupes.",
      "Eau, chargeurs et confort climatisé à bord pour la longue distance.",
      "Très demandé en saison : réservez tôt pour les arrivées de juin à septembre.",
    ],
    faq: [
      {
        q: "Combien coûte un transfert aéroport de Nice – Saint-Tropez ?",
        a: "Le forfait dépend de la catégorie de véhicule et est affiché ci-dessus. Il couvre la totalité du trajet, péages et accueil inclus, sans frais kilométrique surprise.",
      },
      {
        q: "Quelle est la durée du trajet vers Saint-Tropez ?",
        a: "Comptez environ 1h30 hors saison et jusqu'à 2h30 lors des week-ends d'été et pics de trafic dans le golfe de Saint-Tropez.",
      },
      {
        q: "Quel véhicule choisir pour ce transfert ?",
        a: "Pour la longue distance avec bagages, la Class V (van premium) est la plus confortable. Les berlines Class E et S conviennent pour 2 à 3 passagers voyageant léger.",
      },
    ],
    seo: {
      title:
        "Transfert Aéroport de Nice ⇄ Saint-Tropez — VTC privé | KS Rent Services",
      description:
        "Transfert privé aéroport de Nice – Saint-Tropez avec chauffeur. Environ 1h30, tarif forfaitaire tout compris par véhicule. Réservez votre trajet golfe.",
      canonical: "/chauffeur/transferts/aeroport-nice-saint-tropez",
    },
  },
  {
    slug: "aeroport-nice-saint-jean-cap-ferrat",
    fromId: "NCE_AIRPORT",
    toId: "ST_JEAN_CAP_FERRAT",
    distanceKm: 22,
    durationMin: 30,
    lead: "Transfert privé entre l'aéroport de Nice et Saint-Jean-Cap-Ferrat, l'une des presqu'îles les plus prisées de la Riviera. Chauffeur discret, accueil soigné, tarif forfaitaire.",
    highlights: [
      "Desserte des villas et hôtels de la presqu'île (Grand-Hôtel du Cap-Ferrat, Royal-Riviera…).",
      "Trajet court d'environ 30 minutes par la basse corniche.",
      "Service adapté à une clientèle privée recherchant confidentialité.",
      "Coordination possible avec gardiens / conciergeries de villa.",
    ],
    faq: [
      {
        q: "Quel est le prix d'un transfert aéroport de Nice – Saint-Jean-Cap-Ferrat ?",
        a: "Forfait fixe par catégorie, affiché ci-dessus, garanti à la réservation. Pas de supplément kilométrique ni de frais d'accueil cachés.",
      },
      {
        q: "Desservez-vous les villas privées de la presqu'île ?",
        a: "Oui, nous assurons la dépose et la prise en charge directement à l'adresse de la villa ou de l'hôtel, en coordination avec votre conciergerie si besoin.",
      },
      {
        q: "Le trajet est-il rapide depuis l'aéroport ?",
        a: "Oui, environ 30 minutes par la basse corniche hors heures de pointe. Un itinéraire panoramique peut être proposé sur demande.",
      },
    ],
    seo: {
      title:
        "Transfert Aéroport de Nice ⇄ Saint-Jean-Cap-Ferrat | KS Rent Services",
      description:
        "Transfert privé aéroport de Nice – Saint-Jean-Cap-Ferrat avec chauffeur. Environ 30 min, tarif forfaitaire par véhicule, dépose villa/hôtel. Réservez.",
      canonical: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
  },
  {
    slug: "monaco-saint-tropez",
    fromId: "MONACO",
    toId: "SAINT_TROPEZ",
    distanceKm: 140,
    durationMin: 120,
    lead: "Transfert longue distance entre Monaco et Saint-Tropez avec chauffeur privé. Deux pôles de prestige reliés en confort, sur un trajet d'environ deux heures à tarif forfaitaire.",
    highlights: [
      "Liaison directe entre la Principauté et le golfe de Saint-Tropez.",
      "Environ 2h de route par l'A8 puis la route du littoral varois.",
      "Class V conseillée pour le confort et les bagages sur longue distance.",
      "Idéal yachting, événements et séjours combinés Monaco / Saint-Tropez.",
    ],
    faq: [
      {
        q: "Combien coûte un transfert Monaco – Saint-Tropez ?",
        a: "Le forfait, affiché ci-dessus par véhicule, couvre l'intégralité du trajet, péages et accueil compris. Le prix est verrouillé à la réservation.",
      },
      {
        q: "Combien de temps dure le trajet Monaco – Saint-Tropez ?",
        a: "Environ 2h hors saison, davantage lors des pics estivaux. Nous adaptons l'heure de départ pour fluidifier l'arrivée dans le golfe.",
      },
      {
        q: "Peut-on faire un arrêt en chemin ?",
        a: "Oui, un arrêt (déjeuner, point de vue, hôtel) est possible. Indiquez-le à la réservation pour l'intégrer au déroulé du transfert.",
      },
    ],
    seo: {
      title:
        "Transfert Monaco ⇄ Saint-Tropez — VTC chauffeur privé | KS Rent Services",
      description:
        "Transfert privé Monaco – Saint-Tropez avec chauffeur. Environ 2h, tarif forfaitaire tout compris par véhicule. Liaison prestige Riviera. Réservez en ligne.",
      canonical: "/chauffeur/transferts/monaco-saint-tropez",
    },
  },
  {
    slug: "cannes-monaco",
    fromId: "CANNES",
    toId: "MONACO",
    distanceKm: 55,
    durationMin: 60,
    lead: "Transfert privé entre Cannes et Monaco avec chauffeur. Une liaison phare de la Côte d'Azur, idéale pour les événements, le business et les soirées dans la Principauté.",
    highlights: [
      "Environ 1h de trajet par l'A8 selon le trafic.",
      "Parfait pour le Grand Prix de Monaco, galas et dîners en Principauté.",
      "Mise à disposition possible sur place plutôt qu'un simple aller.",
      "Retour de nuit assuré en toute sérénité.",
    ],
    faq: [
      {
        q: "Quel est le tarif d'un transfert Cannes – Monaco ?",
        a: "Forfait fixe par catégorie de véhicule, affiché ci-dessus et garanti à la réservation. Péages et accueil inclus, aucun supplément surprise.",
      },
      {
        q: "Proposez-vous la mise à disposition sur place à Monaco ?",
        a: "Oui. Au-delà du simple transfert, nous proposons une mise à disposition à l'heure pour vos rendez-vous et soirées dans la Principauté.",
      },
      {
        q: "Le retour tardif depuis Monaco est-il possible ?",
        a: "Oui, selon disponibilité, y compris après un gala ou un événement. Réservez le retour à l'avance pour garantir le chauffeur.",
      },
    ],
    seo: {
      title:
        "Transfert Cannes ⇄ Monaco — VTC chauffeur privé | KS Rent Services",
      description:
        "Transfert privé Cannes – Monaco avec chauffeur et tarif forfaitaire. Environ 1h, prix fixe par véhicule, mise à disposition possible. Réservez en ligne.",
      canonical: "/chauffeur/transferts/cannes-monaco",
    },
  },
  {
    slug: "cannes-saint-tropez",
    fromId: "CANNES",
    toId: "SAINT_TROPEZ",
    distanceKm: 90,
    durationMin: 80,
    lead: "Transfert privé entre Cannes et Saint-Tropez avec chauffeur. Rejoignez le golfe en confort depuis la Croisette, sur un trajet d'environ 1h20 à tarif forfaitaire.",
    highlights: [
      "Environ 1h20 à 2h selon le trafic estival vers le golfe de Saint-Tropez.",
      "Class V recommandée pour les groupes et les bagages.",
      "Idéal séjours plage, yachting et événements à Saint-Tropez.",
      "Possibilité d'arrêt déjeuner ou shopping en chemin.",
    ],
    faq: [
      {
        q: "Combien coûte un transfert Cannes – Saint-Tropez ?",
        a: "Le forfait par véhicule est affiché ci-dessus, tout compris (péages, accueil). Le prix est fixé et garanti dès la réservation.",
      },
      {
        q: "Quelle est la durée du trajet Cannes – Saint-Tropez ?",
        a: "Environ 1h20 hors saison et jusqu'à 2h lors des week-ends d'été. Nous calons l'heure de départ pour limiter l'attente dans le golfe.",
      },
      {
        q: "Quel véhicule pour aller à Saint-Tropez ?",
        a: "La Class V (van premium) est idéale pour le confort, les groupes et les bagages. Les berlines conviennent pour 2 à 3 voyageurs légers.",
      },
    ],
    seo: {
      title:
        "Transfert Cannes ⇄ Saint-Tropez — VTC chauffeur privé | KS Rent Services",
      description:
        "Transfert privé Cannes – Saint-Tropez avec chauffeur. Environ 1h20, tarif forfaitaire tout compris par véhicule. Liaison golfe de Saint-Tropez. Réservez.",
      canonical: "/chauffeur/transferts/cannes-saint-tropez",
    },
  },
];
